import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
} from "@mui/material";
import { type MRT_ColumnDef } from "material-react-table";
import "../../styles/cases.css";
import CasesIcon from "../../assets/client/cases-icon.svg";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import TaskAltTwoToneIcon from "@mui/icons-material/TaskAltTwoTone";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import Datatable from "../Common/Datatable";
import CustomDataTable, { Column } from "../Common/CustomDataTable";
import data from "../../data/clientAccountCasesData.json";

const ClientAccountCases: React.FC = () => {
  const [_currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "subject",
        header: "Subject",
        enableColumnFilter: false,
        enableSorting: false,
        grow: true,
        size: 400,
      },
      {
        accessorKey: "lastUpdated",
        header: "Last Updated",
        enableColumnFilter: false,
        enableSorting: false,
        size: 140,
      },
      {
        accessorKey: "caseOwner",
        header: "Case Owner",
        enableColumnFilter: false,
        enableSorting: false,
        size: 140,
      },
      {
        accessorKey: "status",
        header: "Status",
        enableColumnFilter: false,
        enableSorting: false,
        size: 120,
      },
    ],
    []
  );

  const caseMetricsColumns = useMemo<Column[]>(
    () => [
      {
        key: "month",
        header: "Month",
        align: "left",
        className: "cases-td-month",
      },
      {
        key: "beginningBacklog",
        header: "BEGINNING BACKLOG",
        align: "center",
      },
      {
        key: "opened",
        header: "OPENED",
        align: "center",
      },
      {
        key: "closed",
        header: "CLOSED",
        align: "center",
      },
      {
        key: "endingBacklog",
        header: "ENDING BACKLOG",
        align: "right",
        render: (value: any) => (
          <span className={value ? "cases-td-ending-backlog" : ""}>
            {value}
          </span>
        ),
      },
    ],
    []
  );

  const cards = [
    {
      icon: (
        <ErrorOutlineRoundedIcon
          sx={{
            color: "#B53737",
            fontSize: 16,
            "& path": {
              stroke: "#B53737",
              strokeWidth: 1,
            },
          }}
        />
      ),
      iconBg: "#FFEDED",
      value: data.status_counts.Open,
      label: "Open Cases",
    },
    {
      icon: (
        <AccessTimeRoundedIcon
          sx={{
            color: "#DF9621",
            fontSize: 16,
            "& path": {
              stroke: "#DF9621",
              strokeWidth: 1,
            },
          }}
        />
      ),
      iconBg: "#FEF6E9",
      value: data.status_counts.Pending,
      label: "Pending Cases",
    },
    {
      icon: (
        <TaskAltTwoToneIcon
          sx={{
            color: "#158F4C",
            fontSize: 16,
            "& path": {
              stroke: "#158F4C",
              strokeWidth: 1,
            },
          }}
        />
      ),
      iconBg: "#E9FAF0",
      value: data.status_counts.Solved,
      label: "Solved Cases",
    },
    {
      icon: (
        <StarOutlineRoundedIcon
          sx={{
            color: "#298EC7",
            fontSize: 16,
            "& path": {
              stroke: "#298EC7",
              strokeWidth: 1,
            },
          }}
        />
      ),
      iconBg: "#EAF5FB",
      value: "43%",
      label: "New Requests",
    },
    {
      icon: (
        <TaskAltTwoToneIcon
          sx={{
            color: "#CA8A04",
            fontSize: 16,
            "& path": {
              stroke: "#CA8A04",
              strokeWidth: 1,
            },
          }}
        />
      ),
      iconBg: "#FFEDE2",
      value: "36%",
      label: "Operational Cases: 15",
    },
    {
      icon: (
        <RepeatRoundedIcon
          sx={{
            color: "#D97706",
            fontSize: 16,
            "& path": {
              stroke: "#D97706",
              strokeWidth: 1,
            },
          }}
        />
      ),
      iconBg: "#FDF1E3",
      value: "21%",
      label: "Repetitive Cases: 9",
    },
  ];

  return (
    <Box sx={{ padding: "8px", overflowY: "auto", maxHeight: "calc(100vh - 240px)", display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Top Summary Card */}
      <Box className="cases-summary">
        <Box className="cases-total-text-box">
          <Box className="cases-total-icon">
            <img src={CasesIcon} alt="summary-icon" />
          </Box>
          <Typography className="cases-total-text"> Based on {data?.total_cases} Cases</Typography>
        </Box>
        <Box className="cases-summary-title">
          <Typography className="cases-summary-header"> Summary</Typography>
        </Box>
        <Typography className="cases-summary-text"> {data?.summary?.ai_overall_summary}</Typography>
        <Box className="cases-fields">
          {cards.map((card) => (
            <Box className="cases-card" key={card.label}>
              <Box
                className="cases-card-icon"
                sx={{ backgroundColor: card.iconBg }}
              >
                {card.icon}
              </Box>

              <Typography className="cases-card-value">
                {card.value}
              </Typography>

              <Typography className="cases-card-label">
                {card.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Case Metrics Table Card */}
      <Box className="cases-summary">
        <Typography className="cases-section-title">
          Case Metrics Table
        </Typography>
        <Typography className="cases-section-subtitle">
          Detailed monthly ledger of opened, closed and backlog volume
        </Typography>

        <CustomDataTable
          data={data.caseMetricsData}
          columns={caseMetricsColumns}
          showPagination={false}
          className="cases-metrics-table"
        />
      </Box>

      {/* List of Cases Card */}
      <Box className="cases-summary">
        <Typography className="cases-section-title" sx={{ marginBottom: "16px" }}>
          List of Cases
        </Typography>

        <Datatable
          columns={columns}
          rows={data.mockCasesList}
          rowCount={data.mockCasesList.length}
          initialSort={[]}
          onPageSizeChange={(pageSize: number) => setPageSize(pageSize)}
          onPaginationChange={(newPageIndex: number) => setCurrentPageIndex(newPageIndex)}
          onSortingChange={()=>{}}
          pageSize={pageSize} 
          timeRange={""} 
          calendarId={null}        
        />
      </Box>
    </Box>
  );
};

export default ClientAccountCases;